import { Link } from "react-router-dom";
import { Play, FileText, Music, Image as ImageIcon, Eye } from "lucide-react";
import { API } from "../lib/api";
import { formatBytes } from "../lib/api";

const PLACEHOLDER_THUMBS = [
  "https://images.unsplash.com/photo-1699446948863-4879e5f97403?w=600&q=70",
  "https://images.unsplash.com/photo-1698940273205-2b903607df31?w=600&q=70",
  "https://images.unsplash.com/photo-1718073398740-9d3484bd6340?w=600&q=70",
  "https://images.unsplash.com/photo-1613908137518-06348dea40e6?w=600&q=70",
];

export const MediaCard = ({ media, index = 0 }) => {
  const isImage = media.media_kind === "image";
  const isVideo = media.media_kind === "video";
  const Icon = { image: ImageIcon, audio: Music, document: FileText, video: Play }[media.media_kind] || FileText;
  const thumb = isImage
    ? `${API}/media/${media.id}/stream`
    : PLACEHOLDER_THUMBS[index % PLACEHOLDER_THUMBS.length];
  const kindLabel = media.media_kind.toUpperCase();

  return (
    <Link
      to={`/media/${media.id}`}
      data-testid={`media-card-${media.id}`}
      className="group block animate-fade-up"
      style={{ animationDelay: `${(index % 8) * 40}ms` }}
    >
      <div className="aspect-video bg-black overflow-hidden relative rounded-sm">
        <img
          src={thumb}
          alt={media.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          onError={(e) => { e.target.src = PLACEHOLDER_THUMBS[0]; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span className="font-mono text-[10px] tracking-wider px-1.5 py-0.5 bg-white text-black font-bold rounded-sm">
            {kindLabel}
          </span>
          {media.is_reel && (
            <span className="font-mono text-[10px] tracking-wider px-1.5 py-0.5 bg-[#E60000] text-white font-bold rounded-sm">
              REEL
            </span>
          )}
        </div>
        <div className="absolute bottom-2 right-2 flex items-center gap-2 text-white text-xs">
          <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-mono">{media.views || 0}</span>
        </div>
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 rounded-full bg-[#E60000] flex items-center justify-center">
              <Play className="w-6 h-6 text-white ml-1" fill="white" />
            </div>
          </div>
        )}
        {!isImage && !isVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="w-12 h-12 text-white/70" strokeWidth={1.2} />
          </div>
        )}
      </div>
      <div className="pt-3">
        <h3 className="font-heading font-bold tracking-tight text-[15px] line-clamp-2 leading-tight">{media.title}</h3>
        {media.tags?.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`media-card-tags-${media.id}`}>
            {media.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-black/15 text-black/60">
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1 text-xs text-black/50">
          <span>{formatBytes(media.size)}</span>
          <span>·</span>
          <span className="truncate">{media.original_filename}</span>
        </div>
      </div>
    </Link>
  );
};
